import type { MutableRefObject } from 'react'
import { toast } from 'sonner'
import { clampOrientedResizeToMaxSize } from '@/lib/transforms/single-object-oriented-resize'
import { MAX_VALUE_PT, type UnitOfMeasure, aboveMaximumError } from '@/lib/units/unit-conversion'

/**
 * Caps a single-object handle-resize at the pasteboard's max dimension (`MAX_VALUE_PT`), same
 * bound and same "Max-value: …" message as the Object Panel's own W/H fields
 * (`aboveMaximumError`) — just surfaced as a toast instead of an inline field error, since there's
 * no field to attach it to while dragging a handle on canvas. Fires at most once per "crossing into
 * the ceiling": `resizeMaxDimensionToastActiveRef` resets as soon as a frame (live or committed)
 * reports a size back under the max, so re-hitting it later toasts again.
 */
export function clampResizeToPasteboardMax(
  params: Omit<Parameters<typeof clampOrientedResizeToMaxSize>[0], 'maxWidth' | 'maxHeight'>,
  resizeMaxDimensionToastActiveRef: MutableRefObject<boolean>,
  unitOfMeasure: UnitOfMeasure
): { x: number; y: number; width: number; height: number } {
  const clamped = clampOrientedResizeToMaxSize({
    ...params,
    maxWidth: MAX_VALUE_PT,
    maxHeight: MAX_VALUE_PT,
  })
  const wasClamped = clamped.width < params.next.width || clamped.height < params.next.height

  if (wasClamped) {
    if (!resizeMaxDimensionToastActiveRef.current) {
      resizeMaxDimensionToastActiveRef.current = true
      toast.error(aboveMaximumError(unitOfMeasure, MAX_VALUE_PT), {
        id: 'resize-max-dimension',
      })
    }
  } else {
    resizeMaxDimensionToastActiveRef.current = false
  }

  return clamped
}
